import { createSlice } from "@reduxjs/toolkit";
import { HTTPRequest } from "../Api";
import { emitCommand, getSocket } from "../socket";
import { setBody, setType } from "./NotificationSlice";

const GameSlice = createSlice({
    name: 'game',
    initialState: {
        currentUser: '',
        game: {},
        messages: [],
        lastSeenMessageTime: 0,
        badgeCount: 0,
        analyticsInstance: null,
        // Who is currently connected to this game's room, and when the players
        // who left were last seen. Pushed by the server, no polling.
        presence: { online: [], lastSeen: {} },
        connected: false
    },
    reducers: {
        setCurrentUser: (state, action) => {
            state.currentUser = action.payload;
        },
        setGame: (state, action) => {
            state.game = action.payload;
        },
        setMessages: (state, action) => {
            state.messages = action.payload;
            state.badgeCount = action.payload.filter(message =>
                new Date(message.createdAt).getTime() > state.lastSeenMessageTime
                && message.senderId !== state.currentUser).length;
        },
        appendMessage: (state, action) => {
            if (state.messages.some(message => message._id === action.payload._id)) {
                return;
            }
            state.messages.push(action.payload);
            if (new Date(action.payload.createdAt).getTime() > state.lastSeenMessageTime
                && action.payload.senderId !== state.currentUser) {
                state.badgeCount += 1;
            }
        },
        setLastSeenMessageTime: (state, action) => {
            state.lastSeenMessageTime = action.payload;
            state.badgeCount = 0;
        },
        setBadgeCount: (state, action) => {
            state.badgeCount = action.payload;
        },
        setAnalyticsInstance: (state, action) => {
            state.analyticsInstance = action.payload;
        },
        setPresence: (state, action) => {
            state.presence = action.payload;
        },
        setConnected: (state, action) => {
            state.connected = action.payload;
        }
    }
})

// Every command shares this shape: emit, hand the result to the caller, and
// surface a failure as a toast instead of silently doing nothing.
const command = (dispatch, event, payload, completed) =>
    emitCommand(event, payload)
        .then(result => {
            if (typeof (completed) === 'function') {
                completed(result);
            }
            return result;
        })
        .catch(error => {
            dispatch(setType('error'));
            dispatch(setBody(error.message));
        });

export function trackGame(id, userId, completed) {
    return (dispatch) => {
        // Geolocation is a third-party HTTP API, so it stays on fetch.
        dispatch(HTTPRequest('GET', 'https://api-bdc.net/data/ip-geolocation?key=bdc_8ecf50a6a22c41feb798edcb472a40ff', null, (data) => {
            command(dispatch, 'session:track', {
                userId: userId,
                gameId: id,
                requestIp: data.ip,
                country: data.country.name,
                countryCode: data.country.isoAlpha2,
                state: data.location.principalSubdivision,
                city: data.location.city,
                latitude: data.latitude,
                longitude: data.longitude
            }, completed);
        }))
    }
}

export function createGame(data, completed) {
    return (dispatch) => {
        command(dispatch, 'game:create', data, (result) => {
            dispatch(setGame(result));
            if (typeof (completed) === 'function') {
                completed(result);
            }
        });
    }
}

/**
 * Joins the game's room. The server answers with the current game, then pushes
 * every later change as `game:update` — this replaces the old 1s poll.
 */
export function subscribeToGame(id, userId, completed) {
    return (dispatch) => {
        command(dispatch, 'game:subscribe', { gameId: id, userId }, (result) => {
            dispatch(setGame(result));
            if (typeof (completed) === 'function') {
                completed(result);
            }
        });
    }
}

export function unsubscribeFromGame() {
    return () => {
        getSocket().emit('game:unsubscribe');
    }
}

export function joinGame(id, data, completed) {
    return (dispatch) => {
        command(dispatch, 'game:join', { gameId: id, player: data }, (result) => {
            dispatch(setGame(result));
            if (typeof (completed) === 'function') {
                completed(result);
            }
        });
    }
}

export function checkAndJoinGame(data, completed) {
    return (dispatch) => {
        command(dispatch, 'game:checkAndJoin', { player: data }, completed);
    }
}

export function setPlayers(id, players, completed) {
    return (dispatch) => {
        command(dispatch, 'game:setPlayers', { gameId: id, players }, (result) => {
            dispatch(setGame(result));
            if (typeof (completed) === 'function') {
                completed(result);
            }
        });
    }
}

export function startGame(id, completed) {
    return (dispatch) => {
        command(dispatch, 'game:start', { gameId: id }, (result) => {
            dispatch(setGame(result));
            if (typeof (completed) === 'function') {
                completed(result);
            }
        });
    }
}

export function makeMove(id, location, userId, card, game, completed) {
    return (dispatch) => {
        command(dispatch, 'game:move', {
            gameId: id,
            pos: location,
            userId: userId,
            card: card,
            team: String(game.players.find(user => user.userId === userId).team).toLowerCase()
        }, completed);
    }
}

/** Creates a game that is already dealt: you against the computer. */
export function createGameWithBots(data, completed) {
    return (dispatch) => {
        command(dispatch, 'game:createWithBots', data, (result) => {
            dispatch(setGame(result));
            if (typeof (completed) === 'function') {
                completed(result);
            }
        });
    }
}

/** A one-on-one room with you seated; it starts as soon as a friend joins. */
export function createChallenge(data, completed) {
    return (dispatch) => {
        command(dispatch, 'game:challenge', data, (result) => {
            dispatch(setGame(result));
            if (typeof (completed) === 'function') {
                completed(result);
            }
        });
    }
}

/** Public, read-only view of a game — what a replay or share page may show. */
export function fetchSummary(id, completed, failed) {
    return () => {
        emitCommand('game:summary', { gameId: id })
            .then(completed)
            .catch(error => typeof (failed) === 'function' && failed(error));
    }
}

/** Drops a computer player into an open lobby. */
export function addBot(id, difficulty, completed) {
    return (dispatch) => {
        command(dispatch, 'game:addBot', { gameId: id, difficulty }, completed);
    }
}

/** Turns in a card whose two board cells are both taken. Does not use the turn. */
export function discardCard(id, userId, card, completed) {
    return (dispatch) => {
        command(dispatch, 'game:discard', { gameId: id, userId, card }, completed);
    }
}

export function passTurn(id, userId, completed) {
    return (dispatch) => {
        command(dispatch, 'game:pass', { gameId: id, userId }, completed);
    }
}

/**
 * Asks for a rematch, or begins one early when `start` is set. The finished
 * game carries the rematch state, so the answer arrives through the room
 * everyone is already in.
 */
export function requestRematch(id, userId, start, completed) {
    return (dispatch) => {
        command(dispatch, 'game:rematch', { gameId: id, userId, start: Boolean(start) }, (result) => {
            dispatch(setGame(result));
            if (typeof (completed) === 'function') {
                completed(result);
            }
        });
    }
}

export function sendFeedback(body, completed) {
    return (dispatch) => {
        command(dispatch, 'feedback:send', body, completed);
    }
}

export function sendMessage(body, completed) {
    return (dispatch) => {
        command(dispatch, 'chat:send', body, completed);
    }
}

export const { setGame, setMessages, appendMessage, setCurrentUser,
    setLastSeenMessageTime, setBadgeCount, setAnalyticsInstance,
    setPresence, setConnected } = GameSlice.actions;

export const currentUserSelector = (state) => state.game.currentUser;
export const gameSelector = (state) => state.game.game;
export const messagesSelector = (state) => state.game.messages;
export const lastSeenMessageTimeSelector = (state) => state.game.lastSeenMessageTime;
export const badgeCountSelector = (state) => state.game.badgeCount;
export const analyticsInstanceSelector = (state) => state.game.analyticsInstance;
export const presenceSelector = (state) => state.game.presence;
export const connectedSelector = (state) => state.game.connected;

export default GameSlice.reducer;
